/**
 * Application Route Constants
 *
 * Use these constants in router configuration and navigation.
 * The `as const` assertion provides exact type inference.
 *
 * Organization: Currently flat structure. When exceeding ~25-30 routes,
 * consider splitting into COMMON_ROUTES, MANAGE_ROUTES, TRAIN_ROUTES, etc.
 *
 * @example
 * // In router.tsx
 * path: APP_ROUTES.HOME
 *
 * @example
 * // In navigation
 * navigate(APP_ROUTES.MODULE_SELECTOR)
 */
export const APP_ROUTES = {
  // Common routes (no module prefix)
  DEFAULT: '/',
  APP_INITIALIZER: '/app-initializer',
  HOME: '/home',
  MODULE_SELECTOR: '/module-selector',
  ORGANIZATION_SELECTOR: '/organization-selector',
  COOKIE_POLICY: '/cookie-policy',
  PROFILE: '/profile',
  ACCESS_PASS: '/access-pass',

  // Auth routes
  LOGIN_INTERNAL: '/login/internal',
  REGISTER: '/register-user',

  // Legacy session-expired URL (now rendered inline); redirects to the landing page
  SESSION_EXPIRED: '/session-expired',

  // Salesforce redirect
  MANAGE_SF_REDIRECT: '/manage/sf-redirect',

  // Etca redirect
  MANAGE_ETCA_REDIRECT: '/manage/etca-redirect',

  // Train module routes
  TRAIN_BASE: '/train',
  TRAIN_OVERVIEW: '/train/overview',
  TRAIN_LIBRARY: '/train/library',
  TRAIN_PILOT_SCHEDULE: '/train/pilot/schedule',
  TRAIN_RESERVATIONS: '/train/reservations',

  // Instruct module routes
  INSTRUCT_BASE: '/instruct',
  INSTRUCT_SCHEDULE_HOME: '/instruct/schedule',
  INSTRUCT_MY_SCHEDULE: '/instruct/schedule/my-schedule',
  INSTRUCT_MY_SCHEDULE_DETAIL: '/instruct/schedule/my-schedule/list/:id',
  INSTRUCT_AVAILABILITY_MANAGER: '/instruct/schedule/availability-manager',
  RESOURCE_LOOKUP: '/instruct/resource-lookup',
  DOCUMENTS_BASE: '/documents',

  // Documents routes (Instruct)
  INSTRUCT_DOCUMENTS: '/instruct/documents',
  INSTRUCT_DOCUMENTS_COMPANIES: '/instruct/documents/companies',
  INSTRUCT_DOCUMENTS_CAE: '/instruct/documents/cae',
  INSTRUCT_DOCUMENTS_CUSTOMER_DOCUMENTS:
    '/instruct/documents/customer-documents',

  // Manage module routes (use resolvePath() to replace :customerId)
  MANAGE_HOME: '/manage/:customerId/home',
  MANAGE_RESERVATION_REQUESTS: '/manage/:customerId/reservation-requests',
  MANAGE_RESERVATION_REQUESTS_NEW:
    '/manage/:customerId/reservation-requests/new',
  MANAGE_RESERVATIONS: '/manage/:customerId/reservations',
  MANAGE_RESERVATIONS_LIST: '/manage/:customerId/reservations/list',
  MANAGE_RESERVATIONS_CALENDAR: '/manage/:customerId/reservations/calendar',
  MANAGE_INSTRUCTORS_MANAGEMENT: '/manage/:customerId/instructors',
  MANAGE_INSTRUCTORS_DETAIL: '/manage/:customerId/instructors/:instructorId',
  MANAGE_INSTRUCTORS_BATCH_MANAGEMENT:
    '/manage/:customerId/instructors/manage-instructors',
  MANAGE_INSTRUCTORS_APPROVAL_LETTER:
    '/manage/:customerId/instructors/approval-letter',
  MANAGE_INVOICES_PAYMENTS: '/manage/:customerId/invoices-and-payments',
  MANAGE_INVOICES_DETAIL:
    '/manage/:customerId/invoices-and-payments/:accountReceivableId',
  MANAGE_RECORDS: '/manage/:customerId/records',
  MANAGE_USERS: '/manage/:customerId/users',
  MANAGE_USERS_CHANGE_REQUEST:
    '/manage/:customerId/users/:userId/change-request',
  MANAGE_COMPANY_PROFILE: '/manage/:customerId/company-profile',

  // Documents routes (Manage — use resolvePath() to replace :customerId)
  MANAGE_DOCUMENTS: '/manage/:customerId/documents',
  MANAGE_DOCUMENTS_COMPANY: '/manage/:customerId/documents/company',
  MANAGE_DOCUMENTS_CUSTOMER_DOCUMENTS:
    '/manage/:customerId/documents/customer-documents',
  MANAGE_DOCUMENTS_CAE: '/manage/:customerId/documents/cae',
  MANAGE_DOCUMENTS_SUBMITTED:
    '/manage/:customerId/documents/submitted-documents',
  MANAGE_DOCUMENTS_SUBMITTED_SUBMIT:
    '/manage/:customerId/documents/submitted-documents/submit-documents',
} as const;

/**
 * Instructors child route segments for nested route definitions.
 */
export const INSTRUCTORS_ROUTE_SEGMENTS = {
  LIST: 'instructors',
  DETAIL: ':instructorId',
  MANAGE_INSTRUCTORS: 'manage-instructors',
  APPROVAL_LETTER: 'approval-letter',
} as const;

/**
 * Profile child route segments for nested route definitions.
 */
export const PROFILE_ROUTE_SEGMENTS = {
  PERSONAL_INFORMATION: 'personal-information',
  NOTIFICATIONS: 'notifications',
  PASSWORD_AND_SECURITY: 'password-and-security',
  EDIT_IDENTITY: 'edit-identity',
  EDIT_CONTACT: 'edit-contact',
  EDIT_OTHER_INFORMATION: 'edit-other-information',
  EDIT_CITIZENSHIP: 'edit-citizenship',
} as const;

/** Type for route values (e.g., '/home', '/overview') */
export type AppRoute = (typeof APP_ROUTES)[keyof typeof APP_ROUTES];

/** Type for route keys (e.g., 'HOME', 'OVERVIEW') */
export type AppRouteKey = keyof typeof APP_ROUTES;

/**
 * Module route paths - used for route matching in guards
 */
export const MODULE_PATHS = {
  MANAGE: '/manage',
  TRAIN: '/train',
  INSTRUCT: '/instruct',
} as const;

export type ModulePath = (typeof MODULE_PATHS)[keyof typeof MODULE_PATHS];
